/**
 * LLM 平台前端 API 客户端
 *
 * 对应后端路由：/api/admin/llm/*
 *
 * 所有接口都需要 admin 身份。
 */

import { platformFetch } from '../../platform/http';

// ============================================================================
// 类型
// ============================================================================

export interface LlmChannel {
  id: string;
  name: string;
  provider: string;
  apiUrl: string;
  apiKey: string;
  isActive: boolean;
  sortOrder: number;
  remark: string | null;
  defaultTotalTimeoutMs: number;
  defaultIdleTimeoutMs: number;
  modelCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface LlmModel {
  id: string;
  channelId: string;
  name: string;
  modelIdString: string;
  isActive: boolean;
  supportsStream: boolean;
  supportsJsonMode: boolean;
  supportsTools: boolean;
  supportsImages: boolean;
  hasReasoning: boolean;
  contextLength: number;
  maxOutputTokens: number;
  promptPriceCredits: number | null;
  completionPriceCredits: number | null;
  remark: string | null;
  channel?: { id: string; name: string; isActive: boolean; provider: string };
  createdAt: string;
  updatedAt: string;
}

export interface LlmFailureLog {
  id: string;
  useCaseCode: string | null;
  modelId: string | null;
  channelId: string | null;
  userId: string | null;
  status: number | null;
  errorCode: string;
  errorMessage: string;
  requestSummary: string | null;
  rawResponse: string | null;
  latencyMs: number | null;
  createdAt: string;
}

export interface FailureLogQuery {
  page?: number;
  limit?: number;
  useCaseCode?: string;
  channelId?: string;
  startDate?: string;
  endDate?: string;
}

// ============================================================================
// 工具
// ============================================================================

async function adminFetch(path: string, options: RequestInit = {}): Promise<Response> {
  // 委托平台统一请求层：修复此前 401 不清会话的行为不一致
  return platformFetch(path, options);
}

async function adminJson<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await adminFetch(path, options);
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.ok === false) {
    const msg = (body && (body.error || body.message)) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body as T;
}

// ============================================================================
// API
// ============================================================================

export const llmPlatformAPI = {
  // ---------- 渠道 ----------

  async listChannels(): Promise<{ data: LlmChannel[] }> {
    return adminJson('/admin/llm/channels');
  },
  async createChannel(payload: Partial<LlmChannel>): Promise<{ data: LlmChannel }> {
    return adminJson('/admin/llm/channels', { method: 'POST', body: JSON.stringify(payload) });
  },
  async updateChannel(id: string, payload: Partial<LlmChannel>): Promise<{ data: LlmChannel }> {
    return adminJson(`/admin/llm/channels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async deleteChannel(id: string): Promise<{ ok: true }> {
    return adminJson(`/admin/llm/channels/${id}`, { method: 'DELETE' });
  },

  // ---------- 模型 ----------

  async listModels(): Promise<{ data: LlmModel[] }> {
    return adminJson('/admin/llm/models');
  },
  async createModel(payload: Partial<LlmModel>): Promise<{ data: LlmModel }> {
    return adminJson('/admin/llm/models', { method: 'POST', body: JSON.stringify(payload) });
  },
  async updateModel(id: string, payload: Partial<LlmModel>): Promise<{ data: LlmModel }> {
    return adminJson(`/admin/llm/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async deleteModel(id: string): Promise<{ ok: true }> {
    return adminJson(`/admin/llm/models/${id}`, { method: 'DELETE' });
  },

  /**
   * 测试调用（非流式）。
   */
  async testModel(
    modelId: string,
    payload: { message: string; systemPrompt?: string }
  ): Promise<{
    ok: true;
    data: {
      content: string;
      reasoningContent?: string;
      finishReason: string;
      usage: { promptTokens: number; completionTokens: number; totalTokens: number };
      raw?: unknown;
    };
    latencyMs: number;
  }> {
    return adminJson(`/admin/llm/models/${modelId}/test`, {
      method: 'POST',
      body: JSON.stringify({ ...payload, stream: false }),
    });
  },

  /**
   * 流式测试调用。
   */
  async streamTestModel(
    modelId: string,
    payload: { message: string; systemPrompt?: string },
    onEvent: (event: any) => void,
    abortSignal?: AbortSignal
  ): Promise<void> {
    const res = await platformFetch(`/admin/llm/models/${modelId}/test`, {
      method: 'POST',
      body: JSON.stringify({ ...payload, stream: true }),
      signal: abortSignal,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    if (!res.body) throw new Error('响应缺少 body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (!line.startsWith('data:')) continue;
        const data = line.slice(5).trim();
        if (data === '[DONE]') return;
        try {
          onEvent(JSON.parse(data));
        } catch {
          // ignore
        }
      }
    }
  },

  // ---------- 失败日志 ----------

  async listFailureLogs(query: FailureLogQuery = {}): Promise<{
    data: LlmFailureLog[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const params = new URLSearchParams();
    if (query.page) params.set('page', String(query.page));
    if (query.limit) params.set('limit', String(query.limit));
    if (query.useCaseCode) params.set('useCaseCode', query.useCaseCode);
    if (query.channelId) params.set('channelId', query.channelId);
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    const qs = params.toString();
    return adminJson(`/admin/llm/failure-logs${qs ? `?${qs}` : ''}`);
  },

  async getFailureLog(id: string): Promise<{ data: LlmFailureLog }> {
    return adminJson(`/admin/llm/failure-logs/${id}`);
  },
};
